import { useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useList } from "@/hooks/useList";
import { api, formatError } from "@/lib/api";

export function LookupTable({ title, path, fields, testId }) {
  const { canWrite, isAdmin } = useAuth();
  const list = useList(path, { pageSize: 200 });
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const [adding, setAdding] = useState(null);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); setAdding(null); setEditId(null); list.reload(); } catch (e) { toast.error(formatError(e)); }
  };
  const inputs = (obj, set) => fields.map((f) => (
    <td key={f.key} className="px-3 py-2"><Input value={obj[f.key] || ""} onChange={(e) => set({ ...obj, [f.key]: e.target.value })} className="h-8" data-testid={`${testId}-input-${f.key}`} /></td>
  ));

  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid={`lookup-${testId}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <h3 className="text-lg font-semibold text-slate-800">{title} <span className="text-sm text-slate-400 font-normal">({list.total})</span></h3>
        {canWrite && <Button size="sm" variant="outline" onClick={() => setAdding(blank)} data-testid={`${testId}-add-button`}><Plus className="h-4 w-4 mr-1" /> Add</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-400 text-left">{fields.map((f) => <th key={f.key} className="px-3 py-2 font-medium">{f.label}</th>)}<th className="px-3 py-2">Active</th><th /></tr></thead>
          <tbody>
            {adding && (
              <tr className="bg-blue-50/40">{inputs(adding, setAdding)}<td /><td className="px-3 py-2 whitespace-nowrap">
                <Button size="icon" variant="ghost" onClick={() => run(() => api.post(path, adding), "Added")} data-testid={`${testId}-save-new`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setAdding(null)}><X className="h-4 w-4" /></Button>
              </td></tr>
            )}
            {list.items.map((r) => editId === r.id ? (
              <tr key={r.id} className="bg-blue-50/40">{inputs(draft, setDraft)}<td /><td className="px-3 py-2 whitespace-nowrap">
                <Button size="icon" variant="ghost" onClick={() => run(() => api.put(`${path}/${r.id}`, draft), "Saved")} data-testid={`${testId}-save-${r.id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setEditId(null)}><X className="h-4 w-4" /></Button>
              </td></tr>
            ) : (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`${testId}-row-${r.id}`}>
                {fields.map((f) => <td key={f.key} className="px-3 py-2 text-slate-700">{r[f.key] || "—"}</td>)}
                <td className="px-3 py-2"><Switch checked={r.active} disabled={!canWrite} onCheckedChange={(v) => run(() => api.put(`${path}/${r.id}`, { active: v }), "Saved")} data-testid={`${testId}-active-${r.id}`} /></td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  {canWrite && <Button size="icon" variant="ghost" onClick={() => { setEditId(r.id); setDraft(r); }} data-testid={`${testId}-edit-${r.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>}
                  {isAdmin && <Button size="icon" variant="ghost" onClick={() => run(() => api.delete(`${path}/${r.id}`), "Deleted")} data-testid={`${testId}-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
